import { CalendarDays, TrendingUp, Users, BarChart3 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { KPICard } from "../../../components/admin/dashboard/KPICard";
import { DailyTrendChart } from "../../../components/admin/dashboard/charts/DailyTrendChart";
import { DoctorWorkloadChart } from "../../../components/admin/dashboard/charts/DoctorWorkloadChart";
import { AppointmentTable } from "../../../components/admin/dashboard/AppointmentTable";
import { Card } from "../../../components/ui/card";
import { Skeleton } from "../../../components/ui/skeleton";
import { useRole } from "../../../contexts/RoleContext";
import type { DashboardStats, DailyTrendData, DoctorWorkloadData, Appointment, Doctor, Department } from "../../../shared/schema";

interface DepartmentHeadDashboardProps {
  dateRange: "today" | "week";
}

export function DepartmentHeadDashboard({ dateRange }: DepartmentHeadDashboardProps) {
  const { currentDepartmentId } = useRole();

  const { data: department } = useQuery<Department>({
    queryKey: ["/api/departments", currentDepartmentId],
    enabled: Boolean(currentDepartmentId),
  });

  const { data: departmentDoctors } = useQuery<Doctor[]>({
    queryKey: ["/api/doctors", { departmentId: currentDepartmentId }],
    enabled: Boolean(currentDepartmentId),
  });
  
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/stats", { departmentId: currentDepartmentId }],
    enabled: Boolean(currentDepartmentId),
  });

  const { data: dailyTrend, isLoading: trendLoading } = useQuery<DailyTrendData[]>({
    queryKey: ["/api/charts/daily-trend", { departmentId: currentDepartmentId }],
    enabled: Boolean(currentDepartmentId),
  });

  const { data: doctorWorkload, isLoading: workloadLoading } = useQuery<DoctorWorkloadData[]>({
    queryKey: ["/api/charts/doctor-workload", { departmentId: currentDepartmentId }],
    enabled: Boolean(currentDepartmentId),
  });
  
  const { data: appointments, isLoading: appointmentsLoading } = useQuery<Appointment[]>({
    queryKey: ["/api/appointments", { departmentId: currentDepartmentId, range: dateRange }],
    enabled: Boolean(currentDepartmentId),
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-6 border border-card-border">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </Card>
            ))}
          </>
        ) : (
          <>
            <KPICard
              title="Lịch hẹn hôm nay"
              value={stats?.todayAppointments || 0}
              subtitle="Tổng số lịch hẹn trong khoa"
              icon={CalendarDays}
              variant="primary"
            />
            <KPICard
              title="Lịch hẹn tuần này"
              value={stats?.weekAppointments || 0}
              subtitle="Tổng số lịch hẹn trong tuần"
              icon={TrendingUp}
              variant="default"
            />
            <KPICard
              title="Trung bình/bác sĩ"
              value={stats?.avgPerDoctor || 0}
              subtitle="Số ca khám trung bình/ngày"
              icon={Users}
              variant="success"
            />
            <KPICard
              title="Bác sĩ hoạt động"
              value={stats?.activeDoctors || 0}
              subtitle={`Trên tổng ${departmentDoctors?.length || 0} bác sĩ`}
              icon={BarChart3}
              variant="warning"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {trendLoading ? (
          <Card className="p-6 border border-card-border">
            <Skeleton className="h-4 w-48 mb-6" />
            <Skeleton className="h-[300px] w-full" />
          </Card>
        ) : (
          <DailyTrendChart 
            data={dailyTrend || []} 
            title={`Xu hướng tuần - ${department?.nameVi || "Khoa"}`}
          />
        )}
        
        {workloadLoading ? (
          <Card className="p-6 border border-card-border">
            <Skeleton className="h-4 w-48 mb-6" />
            <Skeleton className="h-[300px] w-full" />
          </Card>
        ) : (
          <DoctorWorkloadChart 
            data={doctorWorkload || []}
            title="So sánh khối lượng công việc"
          />
        )}
      </div>

      {appointmentsLoading ? (
        <Card className="p-6 border border-card-border">
          <Skeleton className="h-4 w-48 mb-6" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <AppointmentTable
          appointments={appointments || []}
          doctors={departmentDoctors || []}
          showDepartment={false}
          showDoctor={true}
          title={dateRange === "today" ? "Lịch hẹn hôm nay" : "Lịch hẹn tuần này"}
          emptyMessage="Không có lịch hẹn nào trong khoa"
        />
      )}
    </div>
  );
}
